import { reportPlatformError } from "$root/api/error-reporting";
import { type EventPayloadMap, useEventBus } from "$root/event-bus";
import { getDefaultStore, readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { useEffect, useState } from "react";
import { isBotCompute, LAB_COMPUTE_STOP_MS } from "../common";
import {
	type BotCompute,
	type PortfolioLabContextProps,
	type PortfolioLabInvestmentItem,
	PortfolioLabContext,
} from "../ctx";
import { portfolioLabRepository } from "../repository";
import { applyComputeEvent, applyComputeOutcome, dropReplacedCompute, toastComputeStopped } from "../compute";
import { readSessionNow } from "./useBotWriteResync";

/**
 * Finishes the lab's queued computes. Mounted once, by the Lab page.
 *
 * - A `lab-compute` event finishes the column whose `pendingCompute` carries its `computeId`, or, when
 *   another tab or SphereAI queued it, the column waiting at its revision (`applyComputeEvent`). One
 *   at a later revision replaced the column's compute, so the column stops waiting and the user is
 *   told its charts are out of date. One that changes nothing is held for the `/compute` answer it may
 *   have beaten (`earlyComputeEvents`).
 * - The backend sends the event once and does not replay it, so when the stream reopens the column
 *   list is read again: a pending column whose reports now sit at its pending revision is finished
 *   from that, and one the server holds at a later revision stops waiting with the same notice.
 * - Neither drop happens while a save of the column's own is in flight, which may be what moved it.
 * - A SphereAI recalculate seen in this tab's chat (MDOT-25847) marks the columns it names as soon as
 *   the call shows; a named column not loaded yet takes the mark when it arrives. Its settled task
 *   ends them, and every other column it names, from one read of the list (`finishBotCompute`); no
 *   event or stream reopen does.
 * - A compute still out after {@link LAB_COMPUTE_STOP_MS} stops counting as computing and the user is
 *   told; its event or result still applies if it comes later.
 */
export function useLabComputeEvents(): void {
	const ctx = useMandatoryContext(PortfolioLabContext);
	const [heldBotMarks] = useState(() => new Map<string, BotCompute>());

	useEventBus("lab-compute", (event) => {
		if (!applyComputeEvent(ctx, event)) {
			ctx.earlyComputeEvents.hold(event);
		}
	});

	useEventBus("server-events-stream-open", () => {
		finishComputesFromServer(ctx).catch((error) => {
			reportPlatformError(error, "ERROR", "portfolio", { message: "Unable to re-read the lab's pending computes" });
		});
	});

	useEventBus("bot-lab-recalculate-start", (call) => {
		for (const [virtualUuid, mark] of startBotCompute(ctx, call)) {
			heldBotMarks.set(virtualUuid, mark);
		}
	});

	useEventBus("bot-lab-recalculate-settled", (result) => {
		for (const [virtualUuid, mark] of heldBotMarks) {
			if (mark.toolCallId === result.toolCallId) {
				heldBotMarks.delete(virtualUuid);
			}
		}
		finishBotCompute(ctx, result).catch((error) => {
			reportPlatformError(error, "ERROR", "portfolio", { message: "Unable to finish the lab's SphereAI recalculate" });
		});
	});

	useEffect(() => {
		const placeHeldMarks = () => {
			const onScreen = new Set(readAtom(ctx.investments$).map((i) => i.virtualUuid));
			for (const [virtualUuid, mark] of heldBotMarks) {
				if (Date.now() - mark.queuedAt >= LAB_COMPUTE_STOP_MS) {
					heldBotMarks.delete(virtualUuid);
				} else if (onScreen.has(virtualUuid)) {
					heldBotMarks.delete(virtualUuid);
					markBotCompute(ctx, [virtualUuid], mark);
				}
			}
		};
		return getDefaultStore().sub(ctx.investments$, placeHeldMarks);
	}, [ctx, heldBotMarks]);

	useEffect(() => {
		// By column: a compute SphereAI started has no `computeId`.
		const timers = new Map<string, { queuedAt: number; timer: ReturnType<typeof setTimeout> }>();
		const schedule = () => {
			const waiting = new Map<string, number>();
			for (const column of readAtom(ctx.investments$)) {
				const pending = column.pendingCompute;
				if (pending == null || pending.timedOut) {
					continue;
				}
				waiting.set(column.virtualUuid, pending.queuedAt);
				const scheduled = timers.get(column.virtualUuid);
				if (scheduled?.queuedAt !== pending.queuedAt) {
					clearTimeout(scheduled?.timer);
					const delay = Math.max(0, pending.queuedAt + LAB_COMPUTE_STOP_MS - Date.now());
					timers.set(column.virtualUuid, {
						queuedAt: pending.queuedAt,
						timer: setTimeout(() => stopWaiting(ctx, column.virtualUuid, pending.queuedAt), delay),
					});
				}
			}
			for (const [virtualUuid, { queuedAt, timer }] of timers) {
				if (waiting.get(virtualUuid) !== queuedAt) {
					clearTimeout(timer);
					timers.delete(virtualUuid);
				}
			}
		};
		schedule();
		const unsubscribe = getDefaultStore().sub(ctx.investments$, schedule);
		return () => {
			unsubscribe();
			for (const { timer } of timers.values()) {
				clearTimeout(timer);
			}
		};
	}, [ctx]);
}

function stopWaiting(ctx: PortfolioLabContextProps, virtualUuid: string, queuedAt: number): void {
	const column = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
	if (column?.pendingCompute?.queuedAt !== queuedAt || column.pendingCompute.timedOut) {
		return;
	}
	writeAtom(ctx.investments$, (items) =>
		items.map((i) =>
			i.virtualUuid === virtualUuid && i.pendingCompute?.queuedAt === queuedAt
				? { ...i, pendingCompute: { ...i.pendingCompute, timedOut: true } }
				: i,
		),
	);
	toastComputeStopped(column.name);
}

/** Whether the column waits on a compute of the page's own, which its `lab-compute` event finishes. */
function computingForPage(item: PortfolioLabInvestmentItem): boolean {
	return item.pendingCompute != null && !isBotCompute(item.pendingCompute) && !item.pendingCompute.timedOut;
}

function markBotCompute(ctx: PortfolioLabContextProps, virtualUuids: string[], mark: BotCompute): void {
	const targets = new Set(virtualUuids);
	writeAtom(ctx.investments$, (items) =>
		items.map((i) => (targets.has(i.virtualUuid) && !computingForPage(i) ? { ...i, pendingCompute: mark } : i)),
	);
}

/**
 * Marks the columns a SphereAI recalculate names as computing, or every column when it names none,
 * except one computing for the page. Returns the marks for named columns not on screen yet.
 */
function startBotCompute(
	ctx: PortfolioLabContextProps,
	{ toolCallId, columnUuids, scenarioId }: EventPayloadMap["bot-lab-recalculate-start"],
): Map<string, BotCompute> {
	const mark: BotCompute = { toolCallId, queuedAt: Date.now(), scenarioId, timedOut: false };
	const onScreen = new Set(readAtom(ctx.investments$).map((i) => i.virtualUuid));
	const named = columnUuids ?? [...onScreen];
	markBotCompute(
		ctx,
		named.filter((virtualUuid) => onScreen.has(virtualUuid)),
		mark,
	);
	return new Map(named.filter((virtualUuid) => !onScreen.has(virtualUuid)).map((virtualUuid) => [virtualUuid, mark]));
}

/**
 * Ends the columns a SphereAI recalculate's settled task names, each with its reported outcome at the
 * reports revision the server stored, then clears the call's marks on the rest. A column waiting on
 * another compute (the page's, or a later recalculate's) is left to that one, and one the task still
 * reports QUEUED keeps its mark until the {@link LAB_COMPUTE_STOP_MS} stop. A task that failed as a
 * whole only clears its marks: the bot says so in the chat.
 */
async function finishBotCompute(
	ctx: PortfolioLabContextProps,
	{ toolCallId, scenarioId, columns }: EventPayloadMap["bot-lab-recalculate-settled"],
): Promise<void> {
	const ownMark = (item: PortfolioLabInvestmentItem) =>
		item.pendingCompute != null && isBotCompute(item.pendingCompute) && item.pendingCompute.toolCallId === toolCallId;
	const stillComputing = new Set(
		(columns ?? []).flatMap((column) => (column.status === "QUEUED" ? [column.columnUuid] : [])),
	);
	try {
		if (columns == null || columns.length === 0) {
			return;
		}
		const fromServer = new Map((await readSessionNow()).map((column) => [column.uuid, column]));
		for (const { columnUuid, status } of columns) {
			const column = readAtom(ctx.investments$).find((i) => i.virtualUuid === columnUuid);
			const stored = fromServer.get(columnUuid);
			if (column == null || stored == null || status === "QUEUED") {
				continue;
			}
			if (column.pendingCompute != null && !ownMark(column)) {
				continue;
			}
			applyComputeOutcome(
				ctx,
				columnUuid,
				{ status, reportsComputedAtRevision: stored.reportsComputedAtRevision ?? null },
				{ requested: scenarioId, reports: stored.scenarioId ?? null },
			);
		}
	} finally {
		// Also when the read fails: the column then reads out of date rather than computing until the stop.
		writeAtom(ctx.investments$, (items) =>
			items.map((i) => (ownMark(i) && !stillComputing.has(i.virtualUuid) ? { ...i, pendingCompute: null } : i)),
		);
	}
}

/**
 * Finishes, from the server's column list, the pending computes whose event this tab missed: a column
 * whose stored reports have moved to the revision its compute was queued with. A column whose reports
 * already sat at that revision (a recompute under another scenario) cannot be told apart from one
 * still running, so it keeps waiting. The event's status is lost with it, so the column is finished
 * as `OK`, over the scenario the server stored with the reports. A column the server holds at a
 * later revision had its compute replaced by another writer's, so it stops waiting
 * (`dropReplacedCompute`), unless a save of its own is in flight. A compute SphereAI started is left to
 * its call's result.
 */
async function finishComputesFromServer(ctx: PortfolioLabContextProps): Promise<void> {
	if (!readAtom(ctx.investments$).some((i) => i.pendingCompute != null && !isBotCompute(i.pendingCompute))) {
		return;
	}
	const fromServer = await portfolioLabRepository.listVirtualInvestments.fetchQuery();
	const serverColumn = new Map(fromServer.map((column) => [column.uuid, column]));
	for (const column of readAtom(ctx.investments$)) {
		const pending = column.pendingCompute;
		const stored = serverColumn.get(column.virtualUuid);
		if (pending == null || isBotCompute(pending) || stored == null) {
			continue;
		}
		if (
			column.reportsComputedAtRevision !== pending.revision &&
			(stored.reportsComputedAtRevision ?? null) === pending.revision
		) {
			applyComputeOutcome(
				ctx,
				column.virtualUuid,
				{ status: "OK", reportsComputedAtRevision: pending.revision },
				{ requested: pending.scenarioId, reports: stored.scenarioId ?? null },
			);
		} else if (column.pendingWrites === 0 && stored.compositionRevision > pending.revision) {
			dropReplacedCompute(ctx, column.virtualUuid);
		}
	}
}
